import { useAuth0 } from '@auth0/auth0-react';
import React, { Suspense } from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from 'react-router-dom';
import { AppSwitcherProvider } from './components/AppSwitcherProvider';
import { LoginPage } from './components/auth/LoginPage';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { TokenSetter } from './components/auth/TokenSetter';
import { Header } from './components/Header';
import { HomePage } from './components/HomePage';
import { OrgProvider } from './components/org/OrgProvider';
import { PaymentReturnToast } from './components/PaymentReturnToast';
import { ChunkErrorBoundary } from './components/shared/ChunkErrorBoundary';
import { LoadingSpinner } from './components/shared/LoadingSpinner';
import { Sidebar } from './components/sidebar/Sidebar';
import { SidebarProvider } from './components/sidebar/SidebarProvider';
import { SubscriptionBanner } from './components/profile/SubscriptionBanner';
import { SubscriptionModalProvider } from './components/SubscriptionModalProvider';

// Lazy load authenticated app routes
const ClientellRoutes = React.lazy(() =>
  import('./clientell/routes').then(m => ({ default: m.ClientellRoutes }))
);
const ImminoteRoutes = React.lazy(() =>
  import('./imminote/routes').then(m => ({ default: m.ImminoteRoutes }))
);
const BotmakerRoutes = React.lazy(() =>
  import('./botmaker/routes').then(m => ({ default: m.BotmakerRoutes }))
);
const RakineRoutes = React.lazy(() =>
  import('./rakine/routes').then(m => ({ default: m.RakineRoutes }))
);
const WhatchatRoutes = React.lazy(() =>
  import('./whatchat/routes').then(m => ({ default: m.WhatchatRoutes }))
);

const AdminPricingPage = React.lazy(() =>
  import('./admin/AdminPricingPage').then(m => ({
    default: m.AdminPricingPage,
  }))
);
const AppCheckoutPage = React.lazy(() =>
  import('./checkout/AppCheckoutPage').then(m => ({
    default: m.AppCheckoutPage,
  }))
);

const LandingPage = React.lazy(() =>
  import('./components/LandingPage').then(m => ({ default: m.LandingPage }))
);
const PricingPage = React.lazy(() =>
  import('./pricing/PricingPage').then(m => ({ default: m.PricingPage }))
);

const DemosPage = React.lazy(() =>
  import('./components/DemosPage').then(m => ({ default: m.DemosPage }))
);
const FAQPage = React.lazy(() =>
  import('./components/FAQPage').then(m => ({ default: m.FAQPage }))
);
const OptInPage = React.lazy(() =>
  import('./components/OptInPage').then(m => ({ default: m.OptInPage }))
);
const PrivacyPage = React.lazy(() =>
  import('./components/PrivacyPage').then(m => ({ default: m.PrivacyPage }))
);
const TermsPage = React.lazy(() =>
  import('./components/TermsPage').then(m => ({ default: m.TermsPage }))
);

const PublicFooterWrapper = React.lazy(() =>
  import('./components/landing/PublicFooterWrapper').then(m => ({
    default: m.PublicFooterWrapper,
  }))
);
const PublicHeaderWrapper = React.lazy(() =>
  import('./components/landing/PublicFooterWrapper').then(m => ({
    default: m.PublicHeaderWrapper,
  }))
);

const App: React.FC = () => {
  const { isLoading, isAuthenticated } = useAuth0();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <div
        style={{
          margin: '0 auto',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isAuthenticated ? <TokenSetter /> : null}
        <PaymentReturnToast />
        <SidebarProvider>
          <AppHeader isAuthenticated={isAuthenticated} />

          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {/* Sidebar + authenticated content */}
            <OrgProvider>
              <SubscriptionModalProvider>
                <AppSwitcherProvider>
                  <SubscriptionBanner />
                  <div style={{ display: 'flex', flex: 1 }}>
                    {isAuthenticated && <Sidebar />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <ChunkErrorBoundary>
                        <Suspense fallback={<LoadingSpinner />}>
                          <ClientellRoutes />
                          <ImminoteRoutes />
                          <BotmakerRoutes />
                          <RakineRoutes />
                          <WhatchatRoutes />
                        </Suspense>
                      </ChunkErrorBoundary>

                      <Routes>
                        <Route
                          path="/login"
                          element={
                            isAuthenticated ? (
                              <Navigate to="/" />
                            ) : (
                              <LoginPage />
                            )
                          }
                        />
                        <Route
                          path="/add"
                          element={
                            <ProtectedRoute>
                              <HomePage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/manage"
                          element={
                            <ProtectedRoute>
                              <HomePage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/pricing"
                          element={
                            <ChunkErrorBoundary>
                              <Suspense fallback={<LoadingSpinner />}>
                                <PricingPage />
                              </Suspense>
                            </ChunkErrorBoundary>
                          }
                        />
                        <Route
                          path="/faq"
                          element={
                            <ChunkErrorBoundary>
                              <Suspense fallback={<LoadingSpinner />}>
                                <FAQPage />
                              </Suspense>
                            </ChunkErrorBoundary>
                          }
                        />
                        <Route
                          path="/opt-in/:token"
                          element={
                            <ChunkErrorBoundary>
                              <Suspense fallback={<LoadingSpinner />}>
                                <OptInPage />
                              </Suspense>
                            </ChunkErrorBoundary>
                          }
                        />
                        <Route
                          path="/privacy"
                          element={
                            <ChunkErrorBoundary>
                              <Suspense fallback={<LoadingSpinner />}>
                                <PrivacyPage />
                              </Suspense>
                            </ChunkErrorBoundary>
                          }
                        />
                        <Route
                          path="/terms"
                          element={
                            <ChunkErrorBoundary>
                              <Suspense fallback={<LoadingSpinner />}>
                                <TermsPage />
                              </Suspense>
                            </ChunkErrorBoundary>
                          }
                        />
                        <Route
                          path="/demos"
                          element={
                            <ChunkErrorBoundary>
                              <Suspense fallback={<LoadingSpinner />}>
                                <DemosPage />
                              </Suspense>
                            </ChunkErrorBoundary>
                          }
                        />
                        <Route
                          path="/admin/pricing"
                          element={
                            <ProtectedRoute>
                              <ChunkErrorBoundary>
                                <Suspense fallback={<LoadingSpinner />}>
                                  <AdminPricingPage />
                                </Suspense>
                              </ChunkErrorBoundary>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/checkout"
                          element={
                            <ChunkErrorBoundary>
                              <Suspense fallback={<LoadingSpinner />}>
                                <AppCheckoutPage />
                              </Suspense>
                            </ChunkErrorBoundary>
                          }
                        />
                        <Route
                          path="/"
                          element={
                            isAuthenticated ? (
                              <HomePage />
                            ) : (
                              <ChunkErrorBoundary>
                                <Suspense fallback={<LoadingSpinner />}>
                                  <LandingPage />
                                </Suspense>
                              </ChunkErrorBoundary>
                            )
                          }
                        />
                      </Routes>
                    </div>
                  </div>
                </AppSwitcherProvider>
              </SubscriptionModalProvider>
            </OrgProvider>
          </div>

          <AppFooter isAuthenticated={isAuthenticated} />
        </SidebarProvider>
      </div>
    </Router>
  );
};

const AppHeader = ({ isAuthenticated }: { isAuthenticated: boolean }) => {
  const { pathname } = useLocation();
  if (isAuthenticated) return <Header />;
  if (pathname === '/') return null;
  return (
    <Suspense fallback={null}>
      <PublicHeaderWrapper />
    </Suspense>
  );
};

const AppFooter = ({ isAuthenticated }: { isAuthenticated: boolean }) => {
  const { pathname } = useLocation();

  // Authenticated users have sidebar footer — no page-level footer needed
  if (isAuthenticated) return null;

  // Landing page has its own footer with CTA
  if (pathname === '/') return null;

  // Other public pages get the dark Mantine footer
  return (
    <Suspense fallback={null}>
      <PublicFooterWrapper />
    </Suspense>
  );
};

export default App;
